import { useEffect, useState } from "react";
import { Association } from "src/interfaces/Common";
import { PagesID } from "@utils/utilsConfigurations";
import { ServicesURL } from "@utils/utilsApi";
import { useParams } from "react-router-dom";

export const useAddEditProject = (dataDetail?: any) => {
  const { id } = useParams();

  interface Customer {
    id: number;
    business_name: string;
  }

  interface FinancingPlan {
    id: number;
    title: string;
  }

  const [openAssociations, setOpenAssociations] = useState<
    boolean | Association
  >(false);
  const [PMProjectAssociation, setPMProjectAssociation] = useState<any>([]);
  const [customerAssociation, setCustomerAssociation] = useState<any>([]);
  const [scopeAssociation, setScopeAssociation] = useState<any>([]);
  const [subScopeAssociation, setSubScopeAssociation] = useState<any>([]);
  const [costCenterAssociation, setCostCenterAssociation] = useState<any>([]);
  const [financingPlanAssociation, setFinancingPlanAssociation] = useState<any>(
    [],
  );

  const formatCustomer = (customers: any) => {
    const formattedCustomers = customers.map((customer: Customer) => ({
      id: customer.id,
      label: customer.business_name,
    }));
    setCustomerAssociation(formattedCustomers);
  };

  const formatFinancingPlan = (plans: any) => {
    const formattedPlans = plans.map((plan: FinancingPlan) => ({
      id: plan.id,
      label: plan.title,
    }));
    setFinancingPlanAssociation(formattedPlans);
  };

  const formatCostCenter = (cost_center: any) => {
    cost_center.label = cost_center.sap_code;
    setCostCenterAssociation([cost_center]);
  };

  const formatScope = (scope: any) => {
    scope.label = scope.title;
    setScopeAssociation([scope]);
  };

  const formatSubScope = (sub_scope: any) => {
    sub_scope.label = sub_scope.title;
    setSubScopeAssociation([sub_scope]);
  };

  const formatProjectManager = (pm: any) => {
    pm.label = pm.first_name + " " + pm.last_name;
    setPMProjectAssociation([pm]);
  };

  useEffect(() => {
    if (dataDetail?.cost_center) {
      formatCostCenter(dataDetail.cost_center);
    }

    if (dataDetail?.customers) {
      formatCustomer(dataDetail.customers);
    }

    if (dataDetail?.project_manager) {
      formatProjectManager(dataDetail.project_manager);
    }

    if (dataDetail?.sub_scope) {
      formatSubScope(dataDetail.sub_scope);
    }

    if (dataDetail?.scope) {
      formatScope(dataDetail.scope);
    }

    if (dataDetail?.financing_plans) {
      formatFinancingPlan(dataDetail.financing_plans);
    }
  }, [dataDetail]);

  const resetProjectAssociations = () => {
    if (dataDetail) {
      dataDetail.cost_center
        ? formatCostCenter(dataDetail.cost_center)
        : setCostCenterAssociation([]);
      dataDetail.customers
        ? formatCustomer(dataDetail.customers)
        : setCustomerAssociation([]);
      dataDetail.scope
        ? formatScope(dataDetail.scope)
        : setScopeAssociation([]);
      dataDetail.sub_scope
        ? formatSubScope(dataDetail.sub_scope)
        : setSubScopeAssociation([]);
      dataDetail.project_manager
        ? formatProjectManager(dataDetail.project_manager)
        : setPMProjectAssociation([]);
      dataDetail.financing_plans
        ? formatFinancingPlan(dataDetail.financing_plans)
        : setFinancingPlanAssociation([]);
    }
  };

  const setOpenAssociation = (accessorKey: string, value: boolean) => {
    switch (accessorKey) {
      case "project_manager":
        setOpenAssociations(
          value
            ? {
                accessorKey: "project_manager",
                mediaType: "project_manager",
                type: "table",
                titleKey: "pm-project-association",
                pageId: PagesID["corporate.editions.tutors-faculties"],
                service: ServicesURL.faculties + `?corporate_id=${id}`, //&role=TEACHER`,
                multiChoice: false,
              }
            : value,
        );
        break;
      case "customers":
        setOpenAssociations(
          value
            ? {
                accessorKey: "customers",
                mediaType: "customers",
                type: "table",
                titleKey: "customers-association",
                pageId: PagesID["customers"],
                service: ServicesURL.getCustomers,
              }
            : value,
        );
        break;
      case "scope":
        setOpenAssociations(
          value
            ? {
                accessorKey: "scope",
                mediaType: "scope",
                type: "table",
                titleKey: "scope-association",
                pageId: PagesID["scopes-association"],
                service: ServicesURL.getScopes,
                defaultFilters: {
                  cost_center:
                    costCenterAssociation && costCenterAssociation[0]
                      ? costCenterAssociation[0]?.id
                      : undefined,
                },
                multiChoice: false,
              }
            : value,
        );
        break;
      case "sub_scope":
        setOpenAssociations(
          value
            ? {
                accessorKey: "sub_scope",
                mediaType: "sub_scope",
                type: "table",
                titleKey: "sub-scopes-association",
                pageId: PagesID["sub-scopes-association"],
                service:
                  ServicesURL.getScopes +
                  `/${scopeAssociation?.[0]?.id || "1"}`,
                multiChoice: false,
              }
            : value,
        );
        break;
      case "cost_center":
        setOpenAssociations(
          value
            ? {
                accessorKey: "cost_center",
                mediaType: "cost_center",
                type: "table",
                titleKey: "cost-centers-association",
                pageId: PagesID["cost-centers"],
                service: ServicesURL.getCostCenters,
                multiChoice: false,
              }
            : value,
        );
        break;
      case "financing_plans":
        setOpenAssociations(
          value
            ? {
                accessorKey: "financing_plans",
                mediaType: "financing_plans",
                type: "table",
                titleKey: "financing-plans-association",
                pageId: PagesID["corporate.foundedTraining.managePlan.plan"],
                service: `${ServicesURL.getAllPlans.replace(":corporateId", id || "")}`,
              }
            : value,
        );
        break;
    }
  };

  const getValues = (accessorKey: string) => {
    switch (accessorKey) {
      case "project_manager":
        return PMProjectAssociation;
      case "customers":
        return customerAssociation;
      case "scope":
        return scopeAssociation;
      case "sub_scope":
        return subScopeAssociation;
      case "cost_center":
        return costCenterAssociation;
      case "financing_plans":
        return financingPlanAssociation;
    }
  };

  const handleDelete = (accessorKey: string, e: any) => {
    switch (accessorKey) {
      case "project_manager":
        setPMProjectAssociation(
          PMProjectAssociation.filter((element: any) => element.id !== e.id),
        );
        break;
      case "customers":
        setCustomerAssociation(
          customerAssociation.filter((element: any) => element.id !== e.id),
        );
        break;
      case "scope":
        setScopeAssociation(
          scopeAssociation.filter((element: any) => element.id !== e.id),
        );
        setSubScopeAssociation([]);
        break;
      case "sub_scope":
        setSubScopeAssociation(
          subScopeAssociation.filter((element: any) => element.id !== e.id),
        );
        break;
      case "cost_center":
        setCostCenterAssociation(
          costCenterAssociation.filter((element: any) => element.id !== e.id),
        );
        setSubScopeAssociation([]);
        setScopeAssociation([]);
        break;
      case "financing_plans":
        setFinancingPlanAssociation(
          financingPlanAssociation.filter(
            (element: any) => element.id !== e.id,
          ),
        );
        break;
    }
  };

  const handleAssociation = (accessorKey: string, value: any) => {
    switch (accessorKey) {
      case "project_manager":
        const objToPMProjectAssociation: any = Object.keys(value).map(
          (key: string) => {
            if (key.includes("||")) {
              return {
                id: key.split("||")[0],
                label: key.split("||")[1],
              };
            } else {
              return PMProjectAssociation.find((elem: any) => elem.id === +key);
            }
          },
        );
        setPMProjectAssociation(objToPMProjectAssociation);
        break;
      case "customers":
        const objToCustomerAssociation: any = Object.keys(value).map(
          (key: string) => {
            if (key.includes("||")) {
              return {
                id: key.split("||")[0],
                label: key.split("||")[1],
              };
            } else {
              return objToCustomerAssociation.find(
                (elem: any) => elem.id === +key,
              );
            }
          },
        );
        setCustomerAssociation(objToCustomerAssociation);
        break;
      case "scope":
        const objToScopeAssociation: any = Object.keys(value).map(
          (key: string) => {
            if (key.includes("||")) {
              return {
                id: key.split("||")[0],
                label: key.split("||")[1],
              };
            } else {
              return objToScopeAssociation.find(
                (elem: any) => elem.id === +key,
              );
            }
          },
        );
        const oldScope = scopeAssociation[0]?.id;
        setScopeAssociation(objToScopeAssociation);
        if (oldScope != objToScopeAssociation[0]?.id) {
          setSubScopeAssociation([]);
        }
        break;
      case "sub_scope":
        const objToSubScopeAssociation: any = Object.keys(value).map(
          (key: string) => {
            if (key.includes("||")) {
              return {
                id: key.split("||")[0],
                label: key.split("||")[1],
              };
            } else {
              return objToSubScopeAssociation.find(
                (elem: any) => elem.id === +key,
              );
            }
          },
        );
        setSubScopeAssociation(objToSubScopeAssociation);
        break;
      case "cost_center":
        const objToCostCenterAssociation: any = Object.keys(value).map(
          (key: string) => {
            if (key.includes("||")) {
              return {
                id: key.split("||")[0],
                label: key.split("||")[1],
              };
            } else {
              return objToCostCenterAssociation.find(
                (elem: any) => elem.id === +key,
              );
            }
          },
        );
        const oldCost = costCenterAssociation[0]?.id;
        setCostCenterAssociation(objToCostCenterAssociation);
        if (oldCost != objToCostCenterAssociation[0]?.id) {
          setScopeAssociation([]);
          setSubScopeAssociation([]);
        }
        break;
      case "financing_plans":
        const objToFinancingPlanAssociation: any = Object.keys(value).map(
          (key: string) => {
            if (key.includes("||")) {
              return {
                id: key.split("||")[0],
                label: key.split("||")[1],
              };
            } else {
              return financingPlanAssociation.find(
                (elem: any) => elem.id === +key,
              );
            }
          },
        );
        setFinancingPlanAssociation(objToFinancingPlanAssociation);
        break;
    }
  };

  return {
    handleAssociation,
    setOpenAssociation,
    handleDelete,
    getValues,
    resetProjectAssociations,
    setPMProjectAssociation,
    setCustomerAssociation,
    setScopeAssociation,
    setSubScopeAssociation,
    setCostCenterAssociation,
    setFinancingPlanAssociation,
    PMProjectAssociation,
    customerAssociation,
    scopeAssociation,
    subScopeAssociation,
    costCenterAssociation,
    openAssociations,
    financingPlanAssociation,
  };
};
